import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { CURRENCY, billTotals, releaseRefusal, repricedValue } from '@/domain';
import type { Receipt, ReceiptLine } from '@/application/ports/JobOrderRepository';
import { useFinanceSettings } from '@/application/usecases/useFinance';
import {
  useMarkPaid,
  useReceipt,
  useReleaseCase,
  useSaveReceipt,
  useUndoPayment,
} from '@/application/usecases/useOrders';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatDay, labToday } from '@/shared/format/instant';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { useMoney, useMoneyChoice } from '@/ui/money/moneyView';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { shake } from '@/ui/motion/motion';
import { useConfirmAction } from '../shell/useConfirmAction';
import { signedMoney } from './moneyForm';
import { paymentSummaryPath } from './paymentPaths';
import styles from './ReceiptSheet.module.css';

const t = strings.payments;
const inv = strings.invoice;

/** A typed price, or null when it is not one: empty, negative or not a number. */
function parsePrice(text: string): number | null {
  if (text.trim() === '') return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

type Step = 'receipt' | 'confirmPay' | 'confirmUnpay' | 'confirmRelease';

export interface ReceiptSheetProps {
  jobOrderNo: string;
  onClose: () => void;
  /** The case was marked paid. The screen offers Undo, since this sheet is gone by then. */
  onPaid: (jobOrderNo: string) => void;
  /**
   * The case was released to be shipped unpaid. The screen offers Undo; without
   * it the sheet says so and nothing more. The lab's request of 17 October 2026.
   */
  onReleased?: (jobOrderNo: string) => void;
}

/**
 * A case's receipt, read before it is marked paid. Request of 5 October 2026:
 * every item on the case, the teeth it covers, the price of one and what the
 * line comes to, and the total. While the case is unpaid Accounts may change a
 * price for this case alone; the price list is not touched. Mark paid asks once
 * more, with the total, before anything is sent, and saves changed prices first.
 *
 * A paid case's receipt is read-only, and can be marked unpaid again, after the
 * same kind of question.
 *
 * Save prices asks too, since the request of 5 October 2026 (third), section 2:
 * a price that is not a number is refused here first, then the question names
 * the case, how many prices and the new total. Cancel leaves what was typed.
 * Mark paid still asks once, even when prices changed.
 *
 * An invoice since the lab's request of 17 October 2026, item 12: headed with
 * the lab's name and address, the invoice number (the job order), the number
 * on the doctor's paper, the day, whom it is to and the patient, the days the
 * case was received and is due; then the lines, the total, what has been paid
 * and what is owed, in dirhams, with what that is in dollars for a reader who
 * reads dollars. It prints as it reads. Beside Mark paid is Release, item 8:
 * the case may be shipped before it is paid, after the same kind of question.
 */
export function ReceiptSheet({ jobOrderNo, onClose, onPaid, onReleased }: ReceiptSheetProps) {
  const { data: receipt, isPending, isError } = useReceipt(jobOrderNo);
  const settings = useFinanceSettings();
  const save = useSaveReceipt();
  const markPaid = useMarkPaid();
  const undo = useUndoPayment();
  const release = useReleaseCase();
  const ask = useConfirmAction();
  const money = useMoney();
  const choice = useMoneyChoice();

  const [typed, setTyped] = useState<Record<string, string>>({});
  const [step, setStep] = useState<Step>('receipt');
  const [error, setError] = useState<string | null>(null);
  const primary = useRef<HTMLButtonElement>(null);

  const editable = receipt !== undefined && !receipt.paid && receipt.chargeable;

  /** The lines whose price was changed here and not yet saved, at their new price. */
  const changed = useMemo(() => {
    if (receipt === undefined) return [];
    return receipt.lines.flatMap((line) => {
      const text = typed[line.key];
      if (text === undefined) return [];
      const price = parsePrice(text);
      return price === null || price !== line.unitPrice ? [{ key: line.key, price }] : [];
    });
  }, [receipt, typed]);

  const invalid = changed.some((line) => line.price === null);

  function priceOf(line: ReceiptLine): number {
    const text = typed[line.key];
    const price = text === undefined ? null : parsePrice(text);
    return price ?? line.unitPrice;
  }

  const total = useMemo(
    () =>
      receipt === undefined
        ? 0
        : receipt.lines.reduce(
            (sum, line) =>
              sum +
              (typed[line.key] === undefined ? line.value : repricedValue(line, priceOf(line))),
            0,
          ),
    // priceOf reads typed, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [receipt, typed],
  );

  /*
   * What the case owes of that, after its discounts and charges and what has
   * been paid already, by the rule the server uses, so it follows a price as
   * it is typed. The request of 8 October 2026 (fourth). A receipt from an API
   * older than part payments carries neither, and owes its total.
   */
  const bill = billTotals(total, receipt?.adjustments ?? [], [receipt?.paidAmount ?? 0], receipt?.paid === true);
  const adjusted = (receipt?.adjustments?.length ?? 0) > 0 || (receipt?.paidAmount ?? 0) > 0;

  const busy = save.isPending || markPaid.isPending || undo.isPending || release.isPending;

  // Whether the case may be released now, by the rule the server applies.
  const releasable =
    receipt?.invoice !== undefined &&
    releaseRefusal({
      payment: { paid: receipt.paid },
      release: receipt.invoice.release,
      dispatch: { dispatched: receipt.invoice.dispatched },
      archive: { archived: false },
    }) === null;

  function refuse(message: string) {
    setError(message);
    shake(primary.current);
  }

  async function saveChanged(): Promise<boolean> {
    if (changed.length === 0) return true;
    if (invalid) {
      refuse(t.badPrice);
      return false;
    }
    try {
      await save.mutateAsync({
        jobOrderNo,
        lines: changed.map((line) => ({
          key: line.key,
          unitPrice: line.price ?? 0,
        })),
      });
      setTyped({});
      return true;
    } catch (failure) {
      refuse(failureWords(failure));
      return false;
    }
  }

  function onSave() {
    setError(null);
    if (invalid) {
      refuse(t.badPrice);
      return;
    }
    // A refusal lands on the receipt's own line, so the confirmation has nothing to say.
    ask(
      strings.confirm.savePrices(jobOrderNo, changed.length, money(total)),
      async () => {
        if (await saveChanged()) toast(`${jobOrderNo} ${t.pricesSaved}`, { tone: 'success' });
      },
      { confirmLabel: t.savePrices },
    );
  }

  function askToPay() {
    setError(null);
    if (invalid) {
      refuse(t.badPrice);
      return;
    }
    setStep('confirmPay');
  }

  async function pay() {
    setError(null);
    if (!(await saveChanged())) {
      setStep('receipt');
      return;
    }
    try {
      await markPaid.mutateAsync(jobOrderNo);
      onPaid(jobOrderNo);
      onClose();
    } catch (failure) {
      refuse(failureWords(failure));
    }
  }

  /** Released to be shipped before it is paid. Prices typed and not saved are saved first, as for Mark paid. */
  async function doRelease() {
    setError(null);
    if (!(await saveChanged())) {
      setStep('receipt');
      return;
    }
    try {
      await release.mutateAsync(jobOrderNo);
      if (onReleased === undefined) toast(strings.payments.released(jobOrderNo), { tone: 'success' });
      else onReleased(jobOrderNo);
      onClose();
    } catch (failure) {
      refuse(failureWords(failure));
    }
  }

  async function unpay() {
    setError(null);
    try {
      await undo.mutateAsync(jobOrderNo);
      toast(`${jobOrderNo} ${t.paymentUndone}`, { tone: 'success' });
      onClose();
    } catch (failure) {
      refuse(failureWords(failure));
    }
  }

  const footer =
    receipt === undefined ? (
      <div className={sheetStyles.actions}>
        <Button onClick={onClose}>{t.close}</Button>
      </div>
    ) : step === 'confirmPay' ? (
      <div className={sheetStyles.actions}>
        <Button onClick={() => setStep('receipt')} disabled={busy}>
          {t.back}
        </Button>
        <Button
          ref={primary}
          variant="primary"
          className={sheetStyles.primary}
          onClick={() => void pay()}
          disabled={busy}
          loading={busy}
        >
          {t.confirmPaid}
        </Button>
      </div>
    ) : step === 'confirmUnpay' ? (
      <div className={sheetStyles.actions}>
        <Button onClick={() => setStep('receipt')} disabled={busy}>
          {t.back}
        </Button>
        <Button
          ref={primary}
          variant="primary"
          className={sheetStyles.primary}
          onClick={() => void unpay()}
          disabled={busy}
          loading={busy}
        >
          {t.confirmUnpaid}
        </Button>
      </div>
    ) : step === 'confirmRelease' ? (
      <div className={sheetStyles.actions}>
        <Button onClick={() => setStep('receipt')} disabled={busy}>
          {t.back}
        </Button>
        <Button
          ref={primary}
          variant="primary"
          className={sheetStyles.primary}
          onClick={() => void doRelease()}
          disabled={busy}
          loading={busy}
        >
          {inv.confirmRelease}
        </Button>
      </div>
    ) : receipt.paid ? (
      <div className={sheetStyles.actions}>
        <Button onClick={onClose}>{t.close}</Button>
        <Button onClick={() => window.print()}>{inv.print}</Button>
        <Button ref={primary} onClick={() => setStep('confirmUnpay')}>
          {t.markUnpaid}
        </Button>
      </div>
    ) : (
      <div className={sheetStyles.actions}>
        <Button onClick={onClose} disabled={busy}>
          {t.close}
        </Button>
        <Button onClick={() => window.print()} disabled={busy}>
          {inv.print}
        </Button>
        {changed.length > 0 && (
          <Button onClick={onSave} disabled={busy} loading={save.isPending}>
            {t.savePrices}
          </Button>
        )}
        {releasable && (
          <Button
            onClick={() => {
              setError(null);
              if (invalid) {
                refuse(t.badPrice);
                return;
              }
              setStep('confirmRelease');
            }}
            disabled={busy}
          >
            {strings.payments.releaseToShip}
          </Button>
        )}
        <Button
          ref={primary}
          variant="primary"
          className={sheetStyles.primary}
          onClick={askToPay}
          disabled={busy}
        >
          {t.markPaid}
        </Button>
      </div>
    );

  return (
    <BottomSheet
      title={`${inv.title} · ${jobOrderNo}`}
      lead={t.receiptLead}
      onClose={onClose}
      closeLabel={t.close}
      tall
      footer={footer}
      unsaved={
        changed.length > 0 && !busy
          ? {
              question: t.unsavedPrices,
              keep: strings.sheet.keep,
              discard: strings.sheet.discard,
            }
          : null
      }
    >
      {isPending && <SkeletonRows rows={3} label={t.receiptLoading} />}
      {isError && <p className={styles.note}>{strings.errors.server}</p>}

      {receipt !== undefined && (
        <div className={styles.printable}>
          {step === 'confirmPay' && (
            <p className={styles.statement} data-testid="confirm-statement">
              {t.confirmStatement(jobOrderNo, money(adjusted ? bill.balance : total))}
              {changed.length > 0 && <span className={styles.sub}>{t.confirmSavesFirst}</span>}
            </p>
          )}
          {step === 'confirmUnpay' && (
            <p className={styles.statement} data-testid="confirm-statement">
              {t.unpaidStatement(jobOrderNo)}
            </p>
          )}
          {step === 'confirmRelease' && (
            <p className={styles.statement} data-testid="confirm-statement">
              {strings.confirm.releaseCase(jobOrderNo)}
              {changed.length > 0 && <span className={styles.sub}>{t.confirmSavesFirst}</span>}
            </p>
          )}

          <InvoiceHead receipt={receipt} lab={settings.data?.lab} />

          {!receipt.chargeable && <p className={styles.note}>{t.receiptNotCharged}</p>}
          {receipt.paid && receipt.paidAt !== null && (
            <p className={styles.note}>
              {t.receiptPaidOn} {formatDay(receipt.paidAt)}
            </p>
          )}

          <ul className={styles.lines} aria-label={t.receipt}>
            {receipt.lines.map((line) => {
              const price = priceOf(line);
              const edited = typed[line.key] !== undefined;
              const value = edited ? repricedValue(line, price) : line.value;
              return (
                <li key={line.key} className={styles.line}>
                  <div className={styles.what}>
                    <span className={styles.product}>{line.product}</span>
                    {line.category !== null && (
                      <span className={styles.category}>{line.category}</span>
                    )}
                    <span className={styles.teeth}>
                      <span className={styles.caption}>{t.receiptTeeth}</span>{' '}
                      {line.teeth.length > 0 ? line.teeth.join(', ') : t.receiptNoTeeth}
                    </span>
                  </div>
                  <div className={styles.sum}>
                    <span className={styles.qty}>
                      {line.perSpan ? t.receiptPerSpan : `${String(line.units)} ×`}
                    </span>
                    {editable && step === 'receipt' ? (
                      <input
                        className={styles.price}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        aria-label={`${t.receiptPriceOf} ${line.product}`}
                        value={typed[line.key] ?? String(line.unitPrice)}
                        aria-invalid={edited && parsePrice(typed[line.key] ?? '') === null}
                        onChange={(event) => {
                          const next = event.target.value;
                          setTyped((current) => ({
                            ...current,
                            [line.key]: next,
                          }));
                        }}
                      />
                    ) : (
                      <span className={styles.priceText}>{money(line.unitPrice)}</span>
                    )}
                    <span className={styles.value}>= {money(value)}</span>
                    {line.adjustedFrom !== null && (
                      <span className={styles.was}>
                        {t.receiptWas} {money(line.adjustedFrom)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <p className={styles.total}>
            <span>{t.receiptTotal}</span>
            <span data-testid="receipt-total">{money(total)}</span>
          </p>

          {/* A case with a discount, a charge or a payment on it: what it owes is not its total. */}
          {adjusted && (
            <dl className={styles.owed} data-testid="receipt-owed">
              {/* What stands between the total above and what is due, when anything does. */}
              {bill.adjustments !== 0 && (
                <div>
                  <dt>{strings.finance.adjustmentsHeading}</dt>
                  <dd>{signedMoney(bill.adjustments, money)}</dd>
                </div>
              )}
              <div>
                <dt>{strings.finance.receiptDue}</dt>
                <dd>{money(bill.due)}</dd>
              </div>
              <div>
                <dt>{strings.finance.receiptPaid}</dt>
                <dd>{money(bill.paid)}</dd>
              </div>
              <div>
                <dt>{strings.finance.receiptBalance}</dt>
                <dd>{money(bill.balance)}</dd>
              </div>
            </dl>
          )}

          {/* The money the invoice is in, and what it comes to in dollars for a reader who reads dollars. */}
          <p className={styles.currency}>
            {choice.inDollars && choice.rate !== null
              ? inv.inDollarsAt(String(choice.rate))
              : inv.inDirhams(CURRENCY)}
          </p>

          <p className={styles.more}>
            <Link to={paymentSummaryPath(jobOrderNo)} onClick={onClose}>
              {strings.finance.receiptSummary}
            </Link>
          </p>

          {error !== null && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

/**
 * The head of the invoice: the lab's letterhead, the invoice's number and day,
 * the number on the doctor's paper, whom it is to and the patient, and the
 * days the case was received and is due. Whom it is to only for a reader who
 * may see names; the doctor's ID for anybody else. A receipt from an API older
 * than invoices has no head.
 */
function InvoiceHead({ receipt, lab }: { receipt: Receipt; lab: { name: string; address: string; phone: string; email: string } | undefined }) {
  const head = receipt.invoice;
  if (head === undefined) return null;

  const deliverTo = head.deliverTo ?? null;
  const lines =
    deliverTo === null
      ? []
      : deliverTo.address !== null
        ? deliverTo.address.lines
        : deliverTo.deliveryAddress === null
          ? []
          : [deliverTo.deliveryAddress];
  const contact = [lab?.phone, lab?.email].filter((each): each is string => each !== undefined && each !== '');

  return (
    <div className={styles.invoice} data-testid="invoice-head">
      {lab !== undefined && (
        <div className={styles.letterhead}>
          <b>{lab.name}</b>
          {lab.address !== '' && <span>{lab.address}</span>}
          {contact.length > 0 && <span>{contact.join(' · ')}</span>}
        </div>
      )}
      <dl className={styles.invoiceFacts}>
        <div>
          <dt>{inv.number}</dt>
          <dd>{receipt.jobOrderNo}</dd>
        </div>
        <div>
          <dt>{inv.date}</dt>
          <dd>{receipt.paid && receipt.paidAt !== null ? formatDay(receipt.paidAt) : labToday()}</dd>
        </div>
        {head.serialNumber !== null && (
          <div>
            <dt>{inv.serialNumber}</dt>
            <dd>{head.serialNumber}</dd>
          </div>
        )}
        <div>
          <dt>{inv.received}</dt>
          <dd>{formatDay(head.receivedAt)}</dd>
        </div>
        <div>
          <dt>{inv.due}</dt>
          <dd>{head.deliveryDate}</dd>
        </div>
      </dl>
      <div className={styles.billTo}>
        <span className={styles.caption}>{inv.billTo}</span>
        {head.doctorName !== undefined && head.doctorName !== null ? (
          <AddressBlock name={head.doctorName} clinic={deliverTo?.clinic ?? null} lines={lines} />
        ) : (
          <b>{head.doctorCode ?? inv.noDoctor}</b>
        )}
        {deliverTo?.phoneDisplay != null && <span>{deliverTo.phoneDisplay}</span>}
        {head.patientName != null && (
          <span>
            {inv.patient}: <span dir="auto">{head.patientName}</span>
          </span>
        )}
      </div>
      {head.release !== null && !receipt.paid && (
        <p className={styles.note}>{inv.released(head.release.byName, formatDay(head.release.at))}</p>
      )}
    </div>
  );
}
